/**
 * Memory — view + edit the user's central memory.md.
 *
 * What Gaia remembers across conversations is just a markdown file. This
 * page surfaces that file directly so the user has full visibility and can
 * correct anything that's wrong or out of date.
 */

import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Pencil, Save, X } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getMemory, putMemory } from '@/client/lib/memoryApi';
import { MarkdownContent } from '@/client/components/MarkdownContent';

const QUERY_KEY = ['memory'] as const;

export function MemoryView() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: getMemory,
  });

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  // Sync draft with server data when entering edit mode or after a save.
  useEffect(() => {
    if (!editing) setDraft(data ?? '');
  }, [data, editing]);

  const saveMutation = useMutation({
    mutationFn: (content: string) => putMemory(content),
    onSuccess: () => {
      toast.success('Memory saved');
      setEditing(false);
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : 'Failed to save memory'),
  });

  const handleSave = () => saveMutation.mutate(draft);
  const handleCancel = () => {
    setDraft(data ?? '');
    setEditing(false);
  };

  const hasContent = !!(data && data.trim().length > 0);
  // Footer shows whenever the page is interactive — including the empty
  // state, so the user can click Edit to seed memory manually.
  const showFooter = !isLoading && !error;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header — title + description only. Actions live in the footer so
          they don't compete with the page title and stay close to content. */}
      <div className="border-b px-4 py-3">
        <div className="text-sm font-medium">Memory</div>
        <div className="text-[11px] text-muted-foreground">
          What Gaia remembers about you across conversations. Edit anything that's
          wrong or out of date.
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-4 py-3">
        {isLoading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="size-3 animate-spin" /> Loading memory...
          </div>
        ) : error ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            Failed to load memory: {error instanceof Error ? error.message : 'Unknown error'}
          </div>
        ) : editing ? (
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="# User Memory&#10;&#10;Edit this however you like — Gaia will respect your changes the next time you chat."
            className="h-full min-h-[400px] w-full resize-none rounded-lg border bg-background p-3 font-mono text-xs leading-relaxed outline-none focus:ring-1 focus:ring-ring"
            spellCheck={false}
            autoFocus
          />
        ) : hasContent ? (
          <MarkdownContent content={data} />
        ) : (
          <div className="flex flex-col items-center justify-center gap-2 px-4 py-12 text-center text-xs text-muted-foreground">
            <div className="text-sm">No memory yet</div>
            <div className="opacity-70">
              Gaia hasn't recorded anything about you yet. Memory is built up automatically as you chat.
            </div>
          </div>
        )}
      </div>

      {/* Sticky footer — Save/Cancel during edit, Edit button otherwise.
          Always pinned to the bottom of the panel, never scrolls away. */}
      {showFooter && (
        <div className="flex shrink-0 items-center justify-between gap-3 border-t bg-background px-4 py-2.5">
          {editing ? (
            <>
              <div className="text-[11px] text-muted-foreground">
                Editing — your changes will apply to your next conversation.
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <button
                  onClick={handleCancel}
                  disabled={saveMutation.isPending}
                  className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md border px-3 text-xs font-medium transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <X className="size-3.5" />
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saveMutation.isPending}
                  className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {saveMutation.isPending ? (
                    <Loader2 className="size-3.5 animate-spin" />
                  ) : (
                    <Save className="size-3.5" />
                  )}
                  Save
                </button>
              </div>
            </>
          ) : (
            <button
              onClick={() => setEditing(true)}
              className="ml-auto inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md border px-3 text-xs font-medium transition-colors hover:bg-muted"
            >
              <Pencil className="size-3.5" />
              Edit
            </button>
          )}
        </div>
      )}
    </div>
  );
}
